import { useLocation, useNavigate } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StorageManager } from "datastake-daf/src/helpers/StorageManager";

export const defaultFilterKeys = ['search', 'sortDir', 'sortBy', 'timeframe'];

function hasJsonStructure(str) {
  if (typeof str !== 'string') return false;
  try {
    const result = JSON.parse(str);
    const type = Object.prototype.toString.call(result);
    return type === '[object Object]'
      || type === '[object Array]';
  } catch (err) {
    return false;
  }
}

export const getDefaultActiveFilters = (params, selectFiltersConfig, defaultPageSize, defaultFilters = {}, doPagination) => {
  const o = {};

  defaultFilterKeys.forEach((k) => {
    if (params.has(k)) {
      if (hasJsonStructure(params.get(k))) {
        o[k] = JSON.parse(params.get(k));
      } else {
        o[k] = params.get(k);
      }
    } else if (defaultFilters[k]) {
      if (hasJsonStructure(defaultFilters[k])) {
        o[k] = JSON.parse(defaultFilters[k]);
      } else {
        o[k] = defaultFilters[k];
      }
    }
  });

  if (params.has('searchParams')) {
    try {
      o.searchParams = JSON.parse(params.get('searchParams'))
    } catch (e) {
      o.searchParams = params.get('searchParams').split(',');
    }
  }

  if (doPagination) {
    o.page = Number(params.get('page')) || 1;
    o.pageSize = Number(params.get('pageSize')) || defaultPageSize;
  }

  Object.keys(selectFiltersConfig).forEach((k) => {
    if (params.has(k)) {
      if (hasJsonStructure(params.get(k))) {
        o[k] = JSON.parse(params.get(k));
      } else {
        o[k] = params.get(k);
      }
    }
  });

  return o;
}

export const filterParams = (value) => {
  const {
    activeFilters,
    ...rest
  } = value;

  const {
    page,
    pageSize,
    sortDir,
    sortBy,
    search,
    searchParams,
    ...filters
  } = activeFilters || {};

  const params = {};

  params.pagination = JSON.stringify({
    page,
    size: pageSize,
  });

  if (search && searchParams) {
    params.search = JSON.stringify({
      qs: search,
      fields: searchParams,
    });
  }

  if (sortDir && sortBy) {
    params.sort = JSON.stringify({
      dir: sortDir,
      by: sortBy,
    });
  }

  if (Object.keys(filters).length) {
    if (value.sourceId && value.sourceId === 'overview') {
      params.filters = JSON.stringify(filters);
    } else {
      params.filters = JSON.stringify({ ...filters, authorId: undefined });
    }
  }

  return { ...rest, ...params };
};

export const useFilters = ({
  selectFiltersConfig,
  view,
  module,
  filtersConfig,
  defaultPageSize = 10,
  defaultActiveFilters,
  defaultUrlParams = {},
  doPagination = true,
}) => {
  const location = useLocation();
  const goTo = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [activeFilters, setActiveFilters] = useState(
    defaultActiveFilters || getDefaultActiveFilters(params, selectFiltersConfig, defaultPageSize, defaultUrlParams, doPagination)
  );
  const [pagination, setPagination] = useState(
    defaultActiveFilters ? {
      current: defaultActiveFilters.page || 1,
      pageSize: defaultActiveFilters.pageSize || defaultPageSize,
      showSizeChanger: true,
    } : {
      current: !isNaN(Number(params.get('page'))) ? Number(params.get('page')) || 1 : 1,
      pageSize: !isNaN(Number(params.get('pageSize'))) ? Number(params.get('pageSize')) || defaultPageSize : defaultPageSize,
      showSizeChanger: true,
    });

  useEffect(() => {
    if (defaultActiveFilters) {
      setPagination((prev) => ({
        ...prev,
        current: defaultActiveFilters.page || 1,
        pageSize: defaultActiveFilters.pageSize || defaultPageSize,
        showSizeChanger: true,
      }));
      return;
    }
    setPagination((prev) => ({
      ...prev,
      current: !isNaN(Number(params.get('page'))) ? Number(params.get('page')) || 1 : 1,
      pageSize: !isNaN(Number(params.get('pageSize'))) ? Number(params.get('pageSize')) || defaultPageSize : defaultPageSize,
      showSizeChanger: true,
    }));
  }, []);

  const updateQuery = useCallback((filters, page) => {
    const qs = Object.keys(filters)
      .filter((key) => {
        if (!doPagination) {
          return !!filters[key] && key !== 'page' && key !== 'pageSize';
        }

        return !!filters[key];
      })
      .map(key => {
        if (filters[key] && typeof filters[key] === 'object') {
          return `${key}=${JSON.stringify(filters[key])}`;
        }
        return `${key}=${filters[key]}`;
      })
      .join('&');

    if (view) {
      goTo(
        qs !== '' ? `${`/app/${module}/${view}`}?${qs}`
          : doPagination ? `${`/app/${module}/${view}`}?page=${page}`
            : `${`/app/${module}/${view}`}`);
    }
  }, [module, view]);

  const defaultFilters = useMemo(() => {
    const def = {};
    Object.keys(selectFiltersConfig).forEach((key) => {
      if (activeFilters[key]) {
        def[key] = activeFilters[key];
      }
    });
    return def;
  }, [activeFilters, selectFiltersConfig]);

  const newFiltersConfig = useMemo(() => {
    const o = filtersConfig;
    Object.keys(filtersConfig).forEach((key) => {
      if (params.has(key)) {
        o[key] = params.get(key);
      }
    });
    return o;
  }, [params, filtersConfig]);

  useEffect(() => {
    const filteredFilters = Object.keys(activeFilters).reduce((all, key) => {
      if (activeFilters[key]) {
        all[key] = activeFilters[key];
      }
      return all;
    }, {});

    const filters = StorageManager.saveFilters(module, view, { ...filteredFilters });

    if (!filters.pageSize) {
      filters.page = 1;
      filters.pageSize = pagination.pageSize;
    }

    updateQuery(filters, 1);
  }, [activeFilters]);

  const onFiltersChange = useCallback((filters) => {
    const filteredFilters = Object.keys(filters).reduce((all, key) => {
      const conf = selectFiltersConfig[key];
      if (conf) {
        const { show } = conf;
        if (typeof show === 'function') {
          if (show(filters)) {
            return all;
          }
        }
      }

      if (Array.isArray(filters[key]) && !filters[key].length) {
        all[key] = undefined;
        return all;
      }

      all[key] = filters[key];
      return all;
    }, {});

    setActiveFilters((prev) => {
      const o = { ...prev };
      Object.keys(o).forEach((k) => {
        if (Object.keys(selectFiltersConfig).includes(k) || k === 'authorId') {
          delete o[k];
        }
      });
      Object.entries(filteredFilters).forEach(([k, v]) => {
        o[k] = v;
      });

      if (doPagination) {
        o.page = 1;
      }

      return o;
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [selectFiltersConfig]);

  const onTableChange = (page, f, sorter) => {
    const { sortBy, sortDir } = activeFilters;
    const { columnKey, order } = sorter || {};

    let fs = {
      ...activeFilters,
      page: page.pageSize !== activeFilters.pageSize ? 1 : page.current,
      pageSize: page.pageSize || defaultPageSize,
    };

    if (sorter && (columnKey !== sortBy || order !== sortDir)) {
      fs.page = 1;
    }

    if (order) {
      fs = { ...fs, sortDir: order, sortBy: columnKey };
    } else if (sortBy || sortDir) {
      fs = Object.keys(fs).reduce((all, key) => {
        if (key !== 'sortDir' && key !== 'sortBy') {
          if (key === 'page' || key === 'pageSize') {
            all[key] = fs[key];
          } else {
            all[key] = activeFilters[key];
          }
        }
        return all;
      }, {});
    }

    setActiveFilters(fs);

    if (isNaN(page.current)) {
      page.current = 1;
    }

    const filters = StorageManager.saveFilters(module, view, { page: page.current }, true);

    updateQuery(filters, page.current);
    setPagination((prev) => ({
      ...prev,
      current: fs.page,
      pageSize: fs.pageSize || defaultPageSize
    }));
  };

  const onSearch = useCallback((activeFilter, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      page: 1,
      searchParams: activeFilter,
      search: value,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const totalPages = useMemo(() => {
    return Math.ceil(pagination.total / pagination.pageSize);
  }, [pagination]);

  const canGoPrev = useMemo(() => pagination.current !== 1, [pagination]);
  const canGoNext = useMemo(() => pagination.current !== totalPages && totalPages, [pagination, totalPages]);

  const goPrev = () => {
    if (!canGoPrev) {
      return;
    }

    const { sortDir, sortBy } = activeFilters;

    onTableChange(
      { ...pagination, current: pagination.current - 1 },
      undefined,
      sortBy && sortDir ? { sortBy, sortDir } : undefined
    );
  };

  const goNext = () => {
    if (!canGoNext) {
      return;
    }

    const { sortDir, sortBy } = activeFilters;

    onTableChange(
      { ...pagination, current: pagination.current + 1 },
      undefined,
      sortBy && sortDir ? { columnKey: sortBy, order: sortDir } : undefined
    );
  };

  return {
    newFiltersConfig,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    setPagination,
    onFiltersChange,
    setActiveFilters,
  };
};
